import DashboardLayout from "@/components/DashboardLayout";
import { ExportButton } from "@/components/ExportButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

export default function Accounts() {
  const [selectedPlacement, setSelectedPlacement] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data: placements } = trpc.placements.list.useQuery();
  const { data: accountsData, isLoading } = trpc.accounts.list.useQuery({
    placementId: selectedPlacement !== "all" ? parseInt(selectedPlacement) : undefined,
    status: selectedStatus !== "all" ? selectedStatus : undefined,
    search: searchTerm || undefined,
    page,
    pageSize,
  });
  
  // Export data query - fetches all matching accounts for export
  const { data: exportData } = trpc.exports.accounts.useQuery({
    placementId: selectedPlacement !== "all" ? parseInt(selectedPlacement) : undefined,
    status: selectedStatus !== "all" ? selectedStatus : undefined,
  });

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "$0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(num);
  };

  const exportColumns = [
    { key: "account_number", header: "Account Number" },
    { key: "debtor_name", header: "Debtor Name" },
    { key: "placement_name", header: "Placement" },
    { key: "portfolio_type", header: "Portfolio Type" },
    { key: "placement_date", header: "Placement Date" },
    { key: "original_balance", header: "Original Balance", format: (v: any) => `$${parseFloat(v).toFixed(2)}` },
    { key: "current_balance", header: "Current Balance", format: (v: any) => `$${parseFloat(v).toFixed(2)}` },
    { key: "total_collected", header: "Total Collected", format: (v: any) => `$${parseFloat(v).toFixed(2)}` },
    { key: "status", header: "Status" },
    { key: "settlement_amount", header: "Settlement Amount", format: (v: any) => v ? `$${parseFloat(v).toFixed(2)}` : "" },
    { key: "settlement_date", header: "Settlement Date" },
    { key: "settlement_type", header: "Settlement Type" },
    { key: "first_contact_date", header: "First Contact Date" },
    { key: "payment_plan_amount", header: "Payment Plan Amount", format: (v: any) => v ? `$${parseFloat(v).toFixed(2)}` : "" },
    { key: "payment_plan_frequency", header: "Payment Plan Frequency" },
  ];

  const placementData = Array.isArray(placements) ? placements : [];
  const accounts = (accountsData as any)?.accounts || [];
  const total = accountsData?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  const statusColors: Record<string, string> = {
    'active': 'bg-blue-500/10 text-blue-500',
    'settled': 'bg-emerald-500/10 text-emerald-500',
    'payment_plan': 'bg-amber-500/10 text-amber-500',
    'recalled': 'bg-gray-500/10 text-gray-500',
    'uncollectable': 'bg-red-500/10 text-red-500',
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Account Management</h1>
            <p className="text-muted-foreground">View and search all accounts across placements</p>
          </div>
          <ExportButton
            data={Array.isArray(exportData) ? exportData : []}
            filename="accounts_export"
            columns={exportColumns}
          />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by account number or debtor name..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  className="pl-10"
                />
              </div>
              <Select value={selectedPlacement} onValueChange={(v) => { setSelectedPlacement(v); setPage(1); }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Placements" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Placements</SelectItem>
                  {placementData.map((p: any) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.placement_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedStatus} onValueChange={(v) => { setSelectedStatus(v); setPage(1); }}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="settled">Settled</SelectItem>
                  <SelectItem value="payment_plan">Payment Plan</SelectItem>
                  <SelectItem value="recalled">Recalled</SelectItem>
                  <SelectItem value="uncollectable">Uncollectable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Accounts Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Accounts ({total.toLocaleString()} total)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-medium">Account #</th>
                        <th className="text-left py-3 px-2 font-medium">Debtor Name</th>
                        <th className="text-left py-3 px-2 font-medium">Placement</th>
                        <th className="text-right py-3 px-2 font-medium">Original Balance</th>
                        <th className="text-right py-3 px-2 font-medium">Current Balance</th>
                        <th className="text-right py-3 px-2 font-medium">Collected</th>
                        <th className="text-center py-3 px-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((account: any) => (
                        <tr key={account.id} className="border-b hover:bg-muted/50">
                          <td className="py-3 px-2 font-mono text-xs">{account.account_number}</td>
                          <td className="py-3 px-2">
                            <div className="font-medium">{account.debtor_name}</div>
                            <div className="text-xs text-muted-foreground">{account.email}</div>
                          </td>
                          <td className="py-3 px-2 text-muted-foreground">{account.placement_name}</td>
                          <td className="text-right py-3 px-2">{formatCurrency(account.original_balance)}</td>
                          <td className="text-right py-3 px-2">{formatCurrency(account.current_balance)}</td>
                          <td className="text-right py-3 px-2 text-emerald-500">{formatCurrency(account.total_collected)}</td>
                          <td className="text-center py-3 px-2">
                            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${statusColors[account.status] || 'bg-gray-500/10 text-gray-500'}`}>
                              {account.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total.toLocaleString()} accounts
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm px-2">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
